import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Globe,
  MapPin,
  Calendar,
  Briefcase,
  Users,
  Building2,
  Edit,
  Trash2,
  Plus,
  Star,
  Mail,
  Phone,
  Send,
  Shield,
  FileText,
  Eye,
} from 'lucide-react';
import { viewFileInNewTab } from '@/lib/file-actions';
import {
  fetchCompany,
  deleteCompany,
  createContact,
  updateContact,
  deleteContact,
  fetchCompanyFiles,
  deleteCompanyFile,
  fetchInvoicesForCompany,
  type CompanyContact,
  type CreateContactInput,
  type CompanyFile,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency } from '@/lib/utils';
import { ContactFormDialog } from '@/components/companies/ContactFormDialog';
import { SubmissionsList } from '@/components/submissions/SubmissionsList';
import { CompanyFileUploader } from '@/components/files/CompanyFileUploader';
import { PortalUserManager } from '@/components/companies/PortalUserManager';
import { getInitials, capitalizeFirst } from '@/lib/utils';
import { NotesSection } from '@/components/notes';

function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getStatusBadgeVariant(status: string): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (status) {
    case 'open':
      return 'success';
    case 'draft':
      return 'secondary';
    case 'paused':
      return 'warning';
    case 'filled':
      return 'default';
    case 'cancelled':
      return 'destructive';
    default:
      return 'secondary';
  }
}

function CompanyDetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-6 w-32" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<CompanyContact | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const {
    data: company,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['company', id],
    queryFn: () => fetchCompany(id!),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteCompany(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      navigate('/companies');
    },
  });

  const createContactMutation = useMutation({
    mutationFn: (data: CreateContactInput) => createContact(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', id] });
    },
  });

  const updateContactMutation = useMutation({
    mutationFn: ({ contactId, data }: { contactId: string; data: CreateContactInput }) =>
      updateContact(id!, contactId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', id] });
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: (contactId: string) => deleteContact(id!, contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', id] });
    },
  });

  const {
    data: companyFiles,
    isLoading: filesLoading,
  } = useQuery({
    queryKey: ['companyFiles', id],
    queryFn: () => fetchCompanyFiles(id!),
    enabled: !!id,
  });

  const { data: companyInvoicesData } = useQuery({
    queryKey: ['invoices', 'company', id],
    queryFn: () => fetchInvoicesForCompany(id!),
    enabled: !!id,
  });
  const companyInvoices = companyInvoicesData?.data ?? [];

  const deleteFileMutation = useMutation({
    mutationFn: (fileId: string) => deleteCompanyFile(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyFiles', id] });
    },
  });

  if (isLoading) {
    return <CompanyDetailSkeleton />;
  }

  if (error || !company) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-text-primary">Company Not Found</h2>
          <p className="text-text-tertiary mt-2">
            The company you are looking for does not exist or has been removed.
          </p>
          <Button asChild className="mt-4">
            <Link to="/companies">Back to Companies</Link>
          </Button>
        </div>
      </div>
    );
  }

  const handleContactSubmit = async (data: CreateContactInput) => {
    if (editingContact) {
      await updateContactMutation.mutateAsync({ contactId: editingContact.id, data });
    } else {
      await createContactMutation.mutateAsync(data);
    }
  };

  const handleEditContact = (contact: CompanyContact) => {
    setEditingContact(contact);
    setContactDialogOpen(true);
  };

  const handleAddContact = () => {
    setEditingContact(null);
    setContactDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div>
        <Link
          to="/companies"
          className="inline-flex items-center gap-2 text-sm text-text-tertiary hover:text-text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Companies
        </Link>
      </div>

      {/* Company Header */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-6">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <Avatar className="h-16 w-16">
            <AvatarFallback className="text-xl">
              {capitalizeFirst(company.name?.[0]) || '?'}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <h1 className="text-2xl font-bold text-text-primary">{company.name}</h1>
              {company.industry && (
                <Badge variant="secondary">{company.industry}</Badge>
              )}
            </div>

            <div className="flex flex-wrap gap-4 mt-3 text-sm text-text-tertiary">
              {company.website && (
                <a
                  href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-emerald-500"
                >
                  <Globe className="h-4 w-4" />
                  {company.website.replace(/^https?:\/\//, '')}
                </a>
              )}
              {company.address && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {company.address}
                </div>
              )}
              <div className="flex items-center gap-1">
                <Briefcase className="h-4 w-4" />
                {company.jobCount} {company.jobCount === 1 ? 'job' : 'jobs'}
              </div>
              <div className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                {company.contactCount} {company.contactCount === 1 ? 'contact' : 'contacts'}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to={`/companies/${id}/edit`}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Link>
            </Button>
            {!showDeleteConfirm ? (
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(true)}
                className="text-red-500 hover:text-red-400"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            ) : (
              <div className="flex gap-1">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                >
                  Confirm
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview" className="gap-2">
            <Building2 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="contacts" className="gap-2">
            <Users className="h-4 w-4" />
            Contacts ({company.contactCount})
          </TabsTrigger>
          <TabsTrigger value="jobs" className="gap-2">
            <Briefcase className="h-4 w-4" />
            Jobs ({company.jobCount})
          </TabsTrigger>
          <TabsTrigger value="documents" className="gap-2">
            <FileText className="h-4 w-4" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="invoices" className="gap-2">
            <FileText className="h-4 w-4" />
            Invoices ({companyInvoices.length})
          </TabsTrigger>
          <TabsTrigger value="submissions" className="gap-2">
            <Send className="h-4 w-4" />
            Submissions
          </TabsTrigger>
          <TabsTrigger value="portal" className="gap-2">
            <Shield className="h-4 w-4" />
            Portal
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Notes Section (entity-level notes via NotesSection) */}
              <NotesSection entityType="company" entityId={company.id} />

              {/* Quick contacts preview */}
              {company.contacts && company.contacts.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Primary Contacts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {company.contacts.slice(0, 3).map((contact) => (
                        <div key={contact.id} className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="text-xs">
                              {getInitials(contact.firstName, contact.lastName)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">
                                {contact.firstName || ''} {contact.lastName || ''}
                              </span>
                              {contact.isPrimary && (
                                <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                              )}
                            </div>
                            {contact.jobTitle && (
                              <span className="text-xs text-text-tertiary">{contact.jobTitle}</span>
                            )}
                          </div>
                          {contact.email && (
                            <a
                              href={`mailto:${contact.email}`}
                              className="text-text-tertiary hover:text-emerald-500"
                            >
                              <Mail className="h-4 w-4" />
                            </a>
                          )}
                          {contact.phone && (
                            <a
                              href={`tel:${contact.phone}`}
                              className="text-text-tertiary hover:text-emerald-500"
                            >
                              <Phone className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="space-y-4">
                    <div>
                      <dt className="text-sm font-medium text-text-tertiary">Industry</dt>
                      <dd className="text-text-primary mt-1">
                        {company.industry || 'Not specified'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-text-tertiary">Website</dt>
                      <dd className="text-text-primary mt-1">
                        {company.website ? (
                          <a
                            href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-emerald-500 hover:text-emerald-400"
                          >
                            {company.website.replace(/^https?:\/\//, '')}
                          </a>
                        ) : (
                          'Not provided'
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-text-tertiary">Address</dt>
                      <dd className="text-text-primary mt-1">
                        {company.address || 'Not provided'}
                      </dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-text-tertiary" />
                      <div>
                        <dt className="text-sm font-medium text-text-tertiary">Created</dt>
                        <dd className="text-text-primary">{formatDate(company.createdAt)}</dd>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-text-tertiary" />
                      <div>
                        <dt className="text-sm font-medium text-text-tertiary">Last Updated</dt>
                        <dd className="text-text-primary">{formatDate(company.updatedAt)}</dd>
                      </div>
                    </div>
                  </dl>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Contact Persons</CardTitle>
                <Button size="sm" onClick={handleAddContact}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Contact
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {company.contacts && company.contacts.length > 0 ? (
                <div className="space-y-3">
                  {company.contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="flex items-center gap-4 p-4 rounded-lg border border-white/10 bg-white/5"
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarFallback>
                          {getInitials(contact.firstName, contact.lastName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {contact.firstName} {contact.lastName}
                          </span>
                          {contact.isPrimary && (
                            <Badge variant="default" className="text-xs">Primary</Badge>
                          )}
                        </div>
                        {contact.jobTitle && (
                          <p className="text-sm text-text-tertiary">{contact.jobTitle}</p>
                        )}
                        <div className="flex gap-4 mt-1 text-sm text-text-tertiary">
                          {contact.email && (
                            <a
                              href={`mailto:${contact.email}`}
                              className="flex items-center gap-1 hover:text-emerald-500"
                            >
                              <Mail className="h-3.5 w-3.5" />
                              {contact.email}
                            </a>
                          )}
                          {contact.phone && (
                            <a
                              href={`tel:${contact.phone}`}
                              className="flex items-center gap-1 hover:text-emerald-500"
                            >
                              <Phone className="h-3.5 w-3.5" />
                              {contact.phone}
                            </a>
                          )}
                        </div>
                        {contact.notes && (
                          <p className="text-sm text-text-tertiary mt-1">{contact.notes}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditContact(contact)}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteContactMutation.mutate(contact.id)}
                          disabled={deleteContactMutation.isPending}
                          className="text-red-500 hover:text-red-400"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-text-tertiary">
                  <Users className="h-12 w-12 mb-2" />
                  <p>No contacts yet</p>
                  <Button variant="outline" className="mt-4" onClick={handleAddContact}>
                    Add Contact
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Jobs Tab */}
        <TabsContent value="jobs">
          <Card>
            <CardHeader>
              <CardTitle>Linked Jobs</CardTitle>
            </CardHeader>
            <CardContent>
              {company.jobs && company.jobs.length > 0 ? (
                <div className="space-y-3">
                  {company.jobs.map((job) => (
                    <Link
                      key={job.id}
                      to={`/jobs/${job.id}`}
                      className="flex items-center gap-4 p-4 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <Briefcase className="h-5 w-5 text-text-tertiary" />
                      <div className="flex-1">
                        <span className="font-medium">{job.title}</span>
                        <div className="text-sm text-text-tertiary mt-0.5">
                          {job.applicationCount} {job.applicationCount === 1 ? 'application' : 'applications'}
                        </div>
                      </div>
                      <Badge variant={getStatusBadgeVariant(job.status)}>
                        {capitalizeFirst(job.status)}
                      </Badge>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-text-tertiary">
                  <Briefcase className="h-12 w-12 mb-2" />
                  <p>No jobs linked to this company</p>
                  <Button variant="outline" className="mt-4" asChild>
                    <Link to="/jobs/new">Create Job</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Upload Document</CardTitle>
              </CardHeader>
              <CardContent>
                <CompanyFileUploader companyId={id!} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Documents</CardTitle>
              </CardHeader>
              <CardContent>
                {filesLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : companyFiles && companyFiles.length > 0 ? (
                  <div className="space-y-3">
                    {companyFiles.map((file: CompanyFile) => (
                      <div
                        key={file.id}
                        className="flex items-center gap-4 p-4 rounded-lg border border-white/10 bg-white/5"
                      >
                        <FileText className="h-8 w-8 text-text-tertiary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{file.fileName}</p>
                          <div className="flex gap-3 text-xs text-text-tertiary mt-0.5">
                            <Badge variant="secondary" className="text-xs">
                              {file.fileType}
                            </Badge>
                            <span>
                              {file.fileSize < 1024 * 1024
                                ? `${(file.fileSize / 1024).toFixed(1)} KB`
                                : `${(file.fileSize / (1024 * 1024)).toFixed(1)} MB`}
                            </span>
                            {file.uploadedBy && (
                              <span>
                                by {file.uploadedBy.firstName} {file.uploadedBy.lastName}
                              </span>
                            )}
                            <span>
                              {new Date(file.uploadedAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewFileInNewTab(file.id)}
                            title="View document"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteFileMutation.mutate(file.id)}
                            disabled={deleteFileMutation.isPending}
                            className="text-red-500 hover:text-red-400"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-48 text-text-tertiary">
                    <FileText className="h-12 w-12 mb-2" />
                    <p>No documents uploaded yet</p>
                    <p className="text-sm mt-1">Upload contracts, SLAs, NDAs, or other files above</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Invoices</CardTitle>
                <Button
                  size="sm"
                  onClick={() => navigate(`/invoices?companyId=${id}`)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Invoice
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {companyInvoices.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Number</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companyInvoices.map((inv) => (
                      <TableRow
                        key={inv.id}
                        onClick={() => navigate(`/invoices/${inv.id}`)}
                        className="cursor-pointer hover:bg-white/5"
                      >
                        <TableCell className="font-medium">{inv.invoiceNumber}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{capitalizeFirst(inv.status)}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(inv.totalAmount, inv.currency || 'ZAR')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 text-text-tertiary">
                  <FileText className="h-12 w-12 mb-2" />
                  <p>No invoices for this company yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Submissions Tab */}
        <TabsContent value="submissions">
          <Card>
            <CardHeader>
              <CardTitle>Candidate Submissions</CardTitle>
            </CardHeader>
            <CardContent>
              <SubmissionsList companyId={id!} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Portal Tab */}
        <TabsContent value="portal">
          <Card>
            <CardContent className="pt-6">
              <PortalUserManager companyId={id!} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Contact Form Dialog */}
      <ContactFormDialog
        open={contactDialogOpen}
        onOpenChange={setContactDialogOpen}
        contact={editingContact}
        onSubmit={handleContactSubmit}
        isPending={createContactMutation.isPending || updateContactMutation.isPending}
      />
    </div>
  );
}
